import React, { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./UserPermissionsTab.module.scss";
import { MdBlock, MdVisibility, MdEdit, MdFlashOn } from "react-icons/md";
import {
  FiArrowUpCircle,
  FiCheckCircle,
  FiXCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import Axios from "../../../../../_services/caller.service";
import { toast } from "react-toastify";
import PromoteToSupportModal from "./PromoteToSupportModal";
import RightDrawer from "../../../../Rightpanel/RightDrawer";
import { useTranslation } from "react-i18next";

const UserPermissionsTab = () => {
  const { t } = useTranslation();
  // --- Etats ---
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [permissionsData, setPermissionsData] = useState([]);

  const [overridesByUser, setOverridesByUser] = useState({});
  const [effectivePermissionsByUser, setEffectivePermissionsByUser] = useState({});

  // --- Pagination & filtres ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // --- Drawer ---
  const [drawerUserId, setDrawerUserId] = useState(null);

  // --- Promotion vers Support Team ---
  const [promoteTarget, setPromoteTarget] = useState(null);

  // --- Chargement initial ---
  const fetchInitialUsers = async () => {
    try {
      const res = await Axios.get("/permissionsUsers?role=user");
      setUsers(res.data);
    } catch (err) {
      console.error("Erreur chargement utilisateurs :", err);
      toast.error(t("support.loading_users_error"));
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [modsRes, levelsRes] = await Promise.all([
          Axios.get("/getAllModulesWithActions?role=user"),
          Axios.get("/getAllLevelsWithAccess?role=user"),
        ]);
        setPermissionsData(modsRes.data);
        setLevels(levelsRes.data);
      } catch (err) {
        console.error("Erreur chargement permissions :", err);
        toast.error(t("support.loading_permissions_error"));
      }
    };
    fetchAll();
    fetchInitialUsers();
  }, []);

  // --- Charger overrides + permissions effectives d'un user ---
  const loadUserOverridesAndEffective = useCallback(async (userId) => {
    try {
      const [ovRes, epRes] = await Promise.all([
        Axios.get("/getUserOverrides/" + userId),
        Axios.get("/getEffectivePermissions/" + userId),
      ]);
      setOverridesByUser((prev) => ({ ...prev, [userId]: ovRes.data || {} }));
      setEffectivePermissionsByUser((prev) => ({
        ...prev,
        [userId]: epRes.data.permissions || {},
      }));
    } catch {
      setOverridesByUser((prev) => ({ ...prev, [userId]: {} }));
      setEffectivePermissionsByUser((prev) => ({ ...prev, [userId]: {} }));
    }
  }, []);

  // --- Ouvrir le drawer ---
  const openDrawer = useCallback(
    (userId) => {
      setDrawerUserId(userId);
      if (!overridesByUser[userId] || !effectivePermissionsByUser[userId]) {
        loadUserOverridesAndEffective(userId);
      }
    },
    [overridesByUser, effectivePermissionsByUser, loadUserOverridesAndEffective]
  );

  const closeDrawer = useCallback(() => setDrawerUserId(null), []);

  // --- Mise a jour du groupe ---
  const handleGroupChange = async (userId, newLevelId) => {
    const parsedId =
      newLevelId !== "" && !isNaN(parseInt(newLevelId))
        ? parseInt(newLevelId)
        : null;

    try {
      await Axios.post("/updateUserSupportLevel", {
        userId,
        supportLevelId: parsedId,
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, supportLevelId: parsedId } : u
        )
      );
      toast.success(t("support.group_updated"));

      const res = await Axios.get("/getEffectivePermissions/" + userId);
      setEffectivePermissionsByUser((prev) => ({
        ...prev,
        [userId]: res.data.permissions || {},
      }));
    } catch (err) {
      console.error("Erreur mise à jour groupe :", err);
      toast.error(t("support.group_update_error"));
    }
  };

  // --- Toggle override ---
  const handleCheckboxToggle = (userId, actionKey, level) => {
    setOverridesByUser((prevAll) => {
      const prev = prevAll[userId] || {};
      const current = prev[actionKey];
      let nextOverrides;
      if (current === level) {
        const { [actionKey]: _, ...rest } = prev;
        nextOverrides = rest;
      } else {
        nextOverrides = { ...prev, [actionKey]: level };
      }

      Axios.post("/saveUserOverrides", { userId, overrides: nextOverrides })
        .then(async () => {
          toast.success(t("support.override_updated"));
          const res = await Axios.get("/getEffectivePermissions/" + userId);
          setEffectivePermissionsByUser((prevMap) => ({
            ...prevMap,
            [userId]: res.data.permissions || {},
          }));
        })
        .catch(() => toast.error(t("support.error_saving_override")));

      return { ...prevAll, [userId]: nextOverrides };
    });
  };

  // --- Toggle actif / inactif ---
  const toggleUserActive = async (userId, currentActive) => {
    try {
      await Axios.put("/toggleUserActive", { userId, active: !currentActive });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, active: !currentActive } : u
        )
      );
      toast.success(t("support.user_status_updated"));
    } catch {
      toast.error(t("support.error_server"));
    }
  };

  // --- Filtrage & pagination ---
  const filteredUsers = useMemo(() => {
    return users
      .filter((u) => u.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter((u) => {
        if (selectedGroup === "all") return true;
        if (selectedGroup === "") return u.supportLevelId == null;
        return u.supportLevelId === parseInt(selectedGroup);
      })
      .filter((u) => {
        if (activeFilter === "all") return true;
        if (activeFilter === "active") return u.active;
        if (activeFilter === "inactive") return !u.active;
        return true;
      });
  }, [users, searchTerm, selectedGroup, activeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // --- Drawer data ---
  const drawerUser = useMemo(
    () => users.find((u) => u.id === drawerUserId) || null,
    [users, drawerUserId]
  );

  // --- Palette de couleurs par groupe ---
  const GROUP_COLORS = [
    "#7c3aed", "#0ea5e9", "#10b981", "#f59e0b",
    "#ef4444", "#ec4899", "#8b5cf6", "#14b8a6",
    "#f97316", "#6366f1", "#84cc16", "#06b6d4",
  ];

  const getGroupColor = useCallback(
    (levelId) => {
      if (levelId == null) return "#9ca3af"; // gris pour "Aucun"
      const idx = levels.findIndex((l) => l.id === levelId);
      return idx >= 0 ? GROUP_COLORS[idx % GROUP_COLORS.length] : "#9ca3af";
    },
    [levels]
  );

  // --- Helper : nom du niveau ---
  const getLevelName = (levelId) => {
    const lvl = levels.find((l) => l.id === levelId);
    return lvl ? lvl.name : t("support.none");
  };

  // --- Rendu ---
  return (
    <div className={styles.container}>
      {/* Barre de filtres */}
      <div className={styles.controls}>
        <div className={styles.searchSort}>
          <input
            type="text"
            placeholder={t("support.search_by_name")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <select
            value={selectedGroup}
            onChange={(e) => {
              setSelectedGroup(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">{t("support.all_groups")}</option>
            <option value="">{t("support.no_group")}</option>
            {levels.map((lvl) => (
              <option key={lvl.id} value={String(lvl.id)}>
                {lvl.name}
              </option>
            ))}
          </select>
          <select
            value={usersPerPage}
            onChange={(e) => {
              setUsersPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {t("support.per_page", { count: n })}
              </option>
            ))}
          </select>
          <select
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">{t("support.all_statuses")}</option>
            <option value="active">{t("support.active_only")}</option>
            <option value="inactive">{t("support.inactive_only")}</option>
          </select>
        </div>
      </div>

      {/* Légende des couleurs de groupes */}
      {levels.length > 0 && (
        <div className={styles.groupLegend}>
          <span className={styles.groupLegendItem}>
            <span className={styles.groupDot} style={{ background: "#9ca3af" }} />
            {t("support.none")}
          </span>
          {levels.map((lvl, idx) => (
            <span key={lvl.id} className={styles.groupLegendItem}>
              <span
                className={styles.groupDot}
                style={{ background: GROUP_COLORS[idx % GROUP_COLORS.length] }}
              />
              {lvl.name}
            </span>
          ))}
        </div>
      )}

      {/* Liste des utilisateurs */}
      {filteredUsers.length === 0 ? (
        <div className={styles.emptyState}>{t("support.no_user_found")}</div>
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <span>{t("support.user")}</span>
            <span>{t("support.group")}</span>
            <span className={styles.headerActions} />
          </div>

          <div className={styles.rows}>
            {paginatedUsers.map((user) => {
              const levelName = getLevelName(user.supportLevelId);
              const groupColor = getGroupColor(user.supportLevelId);

              return (
                <div
                  key={user.id}
                  className={styles.row + (!user.active ? " " + styles.rowInactive : "")}
                  style={{ borderLeft: "4px solid " + groupColor }}
                  onClick={() => openDrawer(user.id)}
                >
                  {/* Info utilisateur */}
                  <div className={styles.userInfo}>
                    <span className={styles.userName}>{user.name}</span>
                    <span className={styles.userEmail}>{user.email}</span>
                  </div>

                  {/* Groupe */}
                  <div className={styles.groupCell}>
                    <span
                      className={styles.groupDot}
                      style={{ background: groupColor }}
                    />
                    <select
                      className={styles.groupSelect}
                      value={user.supportLevelId != null ? String(user.supportLevelId) : ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleGroupChange(user.id, e.target.value)}
                    >
                      <option value="">{t("support.none")}</option>
                      {levels.map((lvl) => (
                        <option key={lvl.id} value={String(lvl.id)}>
                          {lvl.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Statut + Actions */}
                  <div className={styles.actionsGroup}>
                    <span
                      className={styles.statusIndicator + " " + (user.active ? styles.statusActive : styles.statusInactive) + " has-tooltip"}
                      aria-label={user.active ? t("support.active") : t("support.inactive")}
                    >
                      {user.active ? <FiCheckCircle /> : <FiXCircle />}
                    </span>
                    <button
                      className={styles.btnAction + " " + (user.active ? styles.btnDanger : styles.btnSuccess) + " has-tooltip"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleUserActive(user.id, user.active);
                      }}
                      aria-label={user.active ? t("support.disable") : t("support.enable")}
                    >
                      {user.active ? <FiXCircle /> : <FiCheckCircle />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className={styles.pagination}>
        <button className="has-tooltip" aria-label={t("pagination.first_page")} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
          <FiChevronsLeft />
        </button>
        <button
          className="has-tooltip" aria-label={t("pagination.previous_page")}
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          <FiChevronLeft />
        </button>
        <span>
          {t("support.page_of", { current: currentPage, total: totalPages })}
        </span>
        <button
          className="has-tooltip" aria-label={t("pagination.next_page")}
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <FiChevronRight />
        </button>
        <button className="has-tooltip" aria-label={t("pagination.last_page")} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
          <FiChevronsRight />
        </button>
      </div>

      {/* RightDrawer -- Permissions de l'utilisateur */}
      <RightDrawer
        isOpen={!!drawerUserId}
        onClose={closeDrawer}
        title={drawerUser ? drawerUser.name + " — Permissions" : "Permissions"}
        width="680px"
      >
        {drawerUser && (
          <div className={styles.drawerContent}>
            {/* Infos utilisateur */}
            <div className={styles.drawerSection}>
              <div className={styles.drawerInfoRow}>
                <div className={styles.drawerInfoItem}>
                  <span className={styles.drawerInfoLabel}>{t("common.name")}</span>
                  <span className={styles.drawerInfoValue}>{drawerUser.name}</span>
                </div>
                <div className={styles.drawerInfoItem}>
                  <span className={styles.drawerInfoLabel}>{t("common.email")}</span>
                  <span className={styles.drawerInfoValue}>{drawerUser.email}</span>
                </div>
                <div className={styles.drawerInfoItem}>
                  <span className={styles.drawerInfoLabel}>{t("common.status")}</span>
                  <span
                    className={styles.statusText + " " + (drawerUser.active ? styles.statusActive : styles.statusInactive)}
                  >
                    {drawerUser.active ? <FiCheckCircle /> : <FiXCircle />}
                    {drawerUser.active ? t("support.active") : t("support.inactive")}
                  </span>
                </div>
              </div>
            </div>

            {/* Card groupe avec couleur + bouton promouvoir */}
            {(() => {
              const gc = getGroupColor(drawerUser.supportLevelId);
              return (
                <div
                  className={styles.drawerGroupCard}
                  style={{
                    borderColor: gc,
                    background: gc + "18",
                  }}
                >
                  <div className={styles.drawerGroupCardHeader}>
                    <span className={styles.drawerInfoLabel} style={{ color: gc }}>
                      {t("support.group")}
                    </span>
                    <span className={styles.groupBadgePill} style={{ background: gc }}>
                      {getLevelName(drawerUser.supportLevelId)}
                    </span>
                  </div>
                  <select
                    className={styles.drawerGroupSelect}
                    value={drawerUser.supportLevelId != null ? String(drawerUser.supportLevelId) : ""}
                    onChange={(e) => handleGroupChange(drawerUser.id, e.target.value)}
                  >
                    <option value="">{t("support.none")}</option>
                    {levels.map((lvl) => (
                      <option key={lvl.id} value={String(lvl.id)}>{lvl.name}</option>
                    ))}
                  </select>
                  <button
                    className={styles.btnPromoteDrawer}
                    onClick={() => setPromoteTarget(drawerUser)}
                  >
                    <FiArrowUpCircle /> {t("support.promote_to_support")}
                  </button>
                </div>
              );
            })()}

            {/* Modules & permissions */}
            {permissionsData.map((mod) => {
              const userOverrides = overridesByUser[drawerUser.id] || {};
              const effectivePerms = effectivePermissionsByUser[drawerUser.id] || {};

              return (
                <div key={mod.id} className={styles.drawerModuleCard}>
                  <h3>{mod.name}</h3>
                  <table className={styles.permissionsTable}>
                    <thead>
                      <tr>
                        <th>{t("support.action_header")}</th>
                        <th>{t("support.description_header")}</th>
                        <th className="has-tooltip" aria-label={t("support.effective_permission")}><MdFlashOn className={styles.badgeEffective} /></th>
                        <th className="has-tooltip" aria-label={t("support.no_access")}>
                          <MdBlock className={styles.badgeNone} />
                        </th>
                        <th className="has-tooltip" aria-label={t("support.read_write")}>
                          <MdEdit className={styles.badgeFull} />
                        </th>
                        <th className="has-tooltip" aria-label={t("support.read_only")}>
                          <MdVisibility className={styles.badgeRead} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mod.actions.map((action) => {
                        const ovVal = userOverrides[action.key];
                        const prVal = effectivePerms[action.key] ?? 0;

                        return (
                          <tr key={action.key} className={ovVal !== undefined && ovVal !== null ? styles.rowOverride : ""}>
                            <td className={styles.actionKeyCell}>{action.key}</td>
                            <td className={styles.descCell}>
                              {action.description || "-"}
                            </td>
                            <td className={styles.previewCell}>
                              {prVal === 0 && <MdBlock className={styles.badgeNone} />}
                              {prVal === 2 && <MdEdit className={styles.badgeFull} />}
                              {prVal === 1 && (
                                <MdVisibility className={styles.badgeRead} />
                              )}
                            </td>
                            {[0, 2, 1].map((val) => (
                              <td key={val}>
                                <input
                                  type="checkbox"
                                  checked={ovVal === val}
                                  onChange={() =>
                                    handleCheckboxToggle(drawerUser.id, action.key, val)
                                  }
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </RightDrawer>

      {/* Modale de promotion */}
      {promoteTarget && (
        <PromoteToSupportModal
          user={promoteTarget}
          onClose={() => setPromoteTarget(null)}
          onSuccess={(userId) => {
            setUsers((prev) => prev.filter((u) => u.id !== userId));
            setPromoteTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default UserPermissionsTab;
